import React, { useState, useEffect } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import UserSettingsLayout from '../../../components/UserSettingsLayout';
import ContentBox from '../../../components/ContentBox';

const API_URL = process.env.REACT_APP_API_URL;

function ValueSubscriptions({ userData }) {
    const [subscriptions, setSubscriptions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [removingId, setRemovingId] = useState(null);
    const [removingAll, setRemovingAll] = useState(false);

    useEffect(() => {
        if (!userData) return;
        loadSubscriptions();
    }, [userData]);

    if (!userData) {
        return <Navigate to="/" replace />;
    }

    const loadSubscriptions = async () => {
        setLoading(true);
        try {
            const response = await fetch(`${API_URL}subscriptions/mine`, {
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setSubscriptions(data.subscriptions);
            } else {
                toast.error('Failed to load subscriptions');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setLoading(false);
        }
    };

    const handleUnsubscribe = async (furniId, furniName) => {
        setRemovingId(furniId);
        try {
            const response = await fetch(`${API_URL}subscriptions/${furniId}`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setSubscriptions(prev => prev.filter(s => s.furniId !== furniId));
                toast.success(`Unsubscribed from ${furniName}`);
            } else {
                toast.error(data.message || 'Failed to unsubscribe');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setRemovingId(null);
        }
    };

    const handleUnsubscribeAll = async () => {
        setRemovingAll(true);
        try {
            const response = await fetch(`${API_URL}subscriptions/all`, {
                method: 'DELETE',
                credentials: 'include',
            });
            const data = await response.json();
            if (data.success) {
                setSubscriptions([]);
                toast.success('Unsubscribed from all value updates');
            } else {
                toast.error(data.message || 'Failed to unsubscribe from all');
            }
        } catch {
            toast.error('Could not connect to server');
        } finally {
            setRemovingAll(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('en-GB', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
        });
    };

    return (
        <UserSettingsLayout>
            <ContentBox theme="default" tint="lightskyblue" header="Value Subscriptions">
                {loading ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">Loading...</p>
                ) : subscriptions.length === 0 ? (
                    <p className="text-gray-600 font-['Jersey_10'] text-[16px]">
                        You are not currently subscribed to any furni value updates.
                    </p>
                ) : (
                    <>
                        <div className="flex items-center justify-between mb-4">
                            <p className="text-sm text-gray-500">
                                {subscriptions.length} active subscription{subscriptions.length !== 1 ? 's' : ''}
                            </p>
                            <button
                                onClick={handleUnsubscribeAll}
                                disabled={removingAll}
                                className="bg-red-600 hover:opacity-90 disabled:opacity-50 text-white px-4 py-1.5 font-['Jersey_10'] uppercase text-[14px] tracking-[1px] rounded transition-opacity"
                                style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.25)' }}
                            >
                                {removingAll ? 'Removing...' : 'Unsubscribe All'}
                            </button>
                        </div>

                        <div className="flex flex-col gap-2">
                            {subscriptions.map((sub) => (
                                <div
                                    key={sub.subscriptionId}
                                    className="flex items-center gap-3 bg-white border border-gray-200 rounded px-4 py-3"
                                    style={{ boxShadow: '0 1px 0 0 rgba(0,0,0,0.08)' }}
                                >
                                    <div className="w-10 h-10 flex-shrink-0 bg-gray-100 rounded flex items-center justify-center">
                                        {sub.furniImage ? (
                                            <img
                                                src={sub.furniImage}
                                                alt={sub.furniName}
                                                className="max-w-full max-h-full object-contain"
                                                style={{ imageRendering: 'pixelated' }}
                                            />
                                        ) : (
                                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-gray-400">
                                                <rect x="3" y="3" width="18" height="18" rx="2" />
                                            </svg>
                                        )}
                                    </div>

                                    <div className="flex-1 min-w-0">
                                        <p className="font-['Jersey_10'] text-[16px] text-gray-800 truncate">
                                            {sub.furniName || 'Unknown Item'}
                                        </p>
                                        <p className="text-xs text-gray-400">
                                            Subscribed {formatDate(sub.subscribedAt)}
                                        </p>
                                    </div>

                                    <button
                                        onClick={() => handleUnsubscribe(sub.furniId, sub.furniName)}
                                        disabled={removingId === sub.furniId || removingAll}
                                        className="flex-shrink-0 bg-gray-200 hover:bg-red-100 hover:text-red-600 disabled:opacity-40 text-gray-600 px-3 py-1 font-['Jersey_10'] uppercase text-[13px] tracking-[1px] rounded transition-colors"
                                        style={{ boxShadow: '0 2px 0 0 rgba(0,0,0,0.1)' }}
                                    >
                                        {removingId === sub.furniId ? '...' : 'Remove'}
                                    </button>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </ContentBox>
        </UserSettingsLayout>
    );
}

export default ValueSubscriptions;